// Astra Today's static facts for the server's move generators (#3753, ADR
// 0041): each school's own name and short name (the college pages), and each
// News story that names schools. The server cannot read the frontend, so
// scripts/build-astra-moves-data.mjs writes this derivation to
// cloud_functions/profile_manager_v2/astra_moves_data.json and
// __tests__/astraMovesData.test.js fails when the committed file drifts.
//
// A story names its schools in `schools` (KB ids, #3971: every story tagged
// with the universities it relates to), with `scope` ('national', or one
// state's two letters) for a story about no KB school. A story about one class only
// (Notre Dame's test requirement starts with the class of 2028) carries
// `moves: { fromClass, toClass, onlyClass }`, and one for a state's students
// `moves: { state }` (two letters); one with none of them applies to every
// student who lists the school.

export const kbId = (id) => String(id || '').trim().toLowerCase().replace(/-/g, '_');

// The KB ids a story names, in the KB's underscore form. The ONE reading of
// a story's `schools` (#3971; it replaced `ask.school` / `ask.schools`): the
// server's news move matches these against a student's list, /news's "On
// your list" box does the same (#3931), the /news search names them, and
// Astra (signed in, Lite, the MCP connector) reads them through
// scripts/build_lite_knowledge.mjs, which imports this.
export const storySchoolIds = (s) => [...new Set((Array.isArray(s?.schools) ? s.schools : [])
    .filter(Boolean).map(kbId))];

// A story's scope beside its schools: 'national' (an agency's or a national
// body's change), a state's two letters, or null.
export const storyScope = (s) => (s?.scope === 'national' || /^[A-Z]{2}$/.test(String(s?.scope || '')) ? s.scope : null);

export function deriveAstraMovesData(stories, colleges) {
    const names = {};
    // A short name two schools share is ambiguous in a sentence ("UM" is the
    // University of Miami's and the University of Montana's, and a reader
    // with Michigan on the list reads it as Michigan; #3787): those schools
    // are named in full, and the shorts are listed so the server can
    // un-shorten a source label that starts with one.
    const sorted = [...colleges].filter((c) => c?.kbId && c?.name)
        .sort((a, b) => String(a.kbId).localeCompare(String(b.kbId)));
    const uses = {};
    for (const c of sorted) if (c.shortName) uses[c.shortName] = (uses[c.shortName] || 0) + 1;
    const ambiguous = Object.keys(uses).filter((k) => uses[k] > 1).sort();
    for (const c of sorted) {
        const short = c.shortName && !ambiguous.includes(c.shortName) ? c.shortName : c.name;
        names[c.kbId] = { name: c.name, short };
        if (c.shortName && ambiguous.includes(c.shortName)) names[c.kbId].ambiguous_short = c.shortName;
    }
    const news = [];
    for (const s of [...stories].sort((a, b) => String(a.slug).localeCompare(String(b.slug)))) {
        const schools = storySchoolIds(s);
        if (!s?.slug || !schools.length) continue;
        const src = (s.sources || [])[0] || {};
        news.push({
            slug: s.slug,
            published_at: s.publishedAt || null,
            title: s.title,
            dek: s.listDek || s.dek || '',
            source_line: s.sourceLine || null,
            source_url: src.url || null,
            schools,
            from_class: s.moves?.fromClass ?? null,
            to_class: s.moves?.toClass ?? null,
            only_class: s.moves?.onlyClass ?? null,
            state: s.moves?.state ?? null,
        });
    }
    return { names, news };
}
